import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import {
    FilteredAsyncContainer, 
    FormValue, 
    IconMinus, 
    IconPlus, 
    LocationSelector,
    PageBehaviourButton,
    Sizes,
    useToggle,
} from '@ratehub/base-ui';

import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import RateFetchingOptionsPropType from '../definitions/RateFetchingOptionsPropType';
import fetchFeaturedRatesSortedByLowest from '../functions/fetchFeaturedRatesSortedByLowest';
import MortgageRateSelectorGroup from './MortgageRateSelectorGroup';
import QuestionRateType from './QuestionRateType';
import QuestionRateTerm from './QuestionRateTerm';
import QuestionOccupancy from './QuestionOccupancy';
import QuestionAmortization from './QuestionAmortization';


function RateFilterSelector({
    initialSelectedRate, // TODO: This should work with non-custom rates too: https://ratehub.atlassian.net/browse/EVR-2731
    initialFilters,
    maximumRates, // TODO: Rename this prop to initiallyDisplayedRates when we implement 'show more' button.
    onSelectRate,
    onFetchRates,
    onFilterChange,
    excludedFilters,
    className,
    ...otherProps
}) {
    const isInitialSelectedRateCustom = !initialSelectedRate?.provider;
    const [ filters, setFilters ] = useState(initialFilters); // TODO: Is there a better way to capture form changes?
    const [ isExpanded, toggleExpanded ] = useToggle(false);

    function handleParamChange(changedParams) {
        const updatedFilters = { ...filters, ...changedParams };

        // TODO: Remove temporary fix to address https://ratehub.atlassian.net/browse/EVR-2836 without blocking work.
        if (typeof updatedFilters.term === 'string') {
            updatedFilters.term = parseInt(updatedFilters.term);
        }
        if (typeof updatedFilters.amortization === 'string') {
            updatedFilters.amortization = parseInt(updatedFilters.amortization);
        }

        setFilters(updatedFilters);
        onFilterChange?.(updatedFilters);
    }

    const onFetchValue = useCallback(
        async params => {
            const rates = await fetchFeaturedRatesSortedByLowest(params, { callerName: 'RateFilterSelector.onFetchValue' });

            onFetchRates?.(rates);

            return rates;
        },
        [ onFetchRates ],
    );

    const getFormValues = useCallback(
        values => ({
            ...initialFilters,
            // Don't override initialFilters with nullish values in case the filter control is excluded
            ...excludeNullishValues({
                type: values.type,
                term: values.term,
                intendedUse: values.intendedUse,
                city: values.city,
                amortization: values.amortization,
            }),
        }),
        // We need to do a deep comparison because any consumer could have an unstable initialFilters object.
        [ JSON.stringify(initialFilters) ],
    );

    return (
        <Container 
            {...otherProps}
            className={classNames(className, 'rh-display-flex')}
            data-test-name="rate-filter-selector"
        >
            <FilteredAsyncContainer
                primaryFilters={[
                    <QuestionRateType
                        key="type"
                        name="type"
                        value={initialFilters.type}
                        className="filter"
                        onChange={type => handleParamChange({ type })}
                    />,
                    <QuestionRateTerm
                        key="term"
                        name="term"
                        value={initialFilters.term}
                        className="filter"
                        onChange={term => handleParamChange({ term })}
                    />,
                    <QuestionOccupancy
                        key="intendedUse"
                        name="intendedUse"
                        value={initialFilters.intendedUse}
                        className="filter"
                        onChange={intendedUse => handleParamChange({ intendedUse })}
                    />,
                ].filter(filter => !excludedFilters.includes(filter.props.name))}
                additionalFilters={[
                    <LocationSelector
                        key="city"
                        name="city"
                        label={(
                            <FormattedMessage
                                id="mtg-common.rate-filter-selector.location-label"
                                defaultMessage="Location"
                            />
                        )}
                        value={initialFilters.city}
                        className="filter"
                        onChange={city => handleParamChange({ city: { name: city.name, province: { code: city.province.code } } })}
                    />,
                    <QuestionAmortization
                        key="amortization"
                        name="amortization"
                        value={initialFilters.amortization}
                        className="filter"
                        onChange={amortization => handleParamChange({ amortization })}
                    />,
                ].filter(filter => !excludedFilters.includes(filter.props.name))}

                fetchedValueName="rates"

                onFetchValue={onFetchValue}
                getFormValues={getFormValues}
                className="filters"
            >
                {({ rates = [], type, term }) => (
                    <MortgageRateSelectorGroup
                        providerRates={isExpanded ? rates : rates.slice(0, maximumRates)}
                        initialCustomRate={isInitialSelectedRateCustom ? initialSelectedRate?.value : undefined}
                        onSelectRate={rate => onSelectRate(rate ? { ...rate, type, term } : null)}
                    />
                )}
            </FilteredAsyncContainer>

            <FormValue>
                {({ rates = [] }) => (
                    <If condition={rates.length > maximumRates}>
                        <PageBehaviourButton
                            onClick={toggleExpanded}
                            data-name="RateFilterSelector-toggle-expanded"
                            iconComponent={isExpanded ? IconMinus : IconPlus}
                            iconLocation="left"
                            size="small"
                            message={(
                                <FormattedMessage
                                    id="RateFilterSelector.toggle-expanded"
                                    defaultMessage="{isExpanded, select, true {Show fewer} other {Show more}}"
                                    values={{ isExpanded }}
                                />
                            )}
                            className="toggle-expanded rh-mb-1"
                        />
                    </If>
                )}
            </FormValue>
            
        </Container>
    );
}

RateFilterSelector.propTypes = {
    initialSelectedRate: PropTypes.object,
    initialFilters: RateFetchingOptionsPropType.isRequired,
    maximumRates: PropTypes.number,
    onSelectRate: PropTypes.func.isRequired,
    onFetchRates: PropTypes.func,
    onFilterChange: PropTypes.func,
    excludedFilters: PropTypes.arrayOf(
        PropTypes.oneOf([ 'type', 'term', 'intendedUse', 'city', 'amortization' ]),
    ),
    className: PropTypes.string,
};

RateFilterSelector.defaultProps = {
    initialSelectedRate: undefined,
    maximumRates: 4,
    onFetchRates: undefined,
    onFilterChange: undefined,
    excludedFilters: [ 'city', 'amortization' ], // most usages don't need these filters
    className: undefined,
};

function excludeNullishValues(object) {
    return Object
        .entries(object)
        .reduce((filters, [ filterKey, filterValue ]) => {
            if (filterValue != null) {
                filters[filterKey] = filterValue;
            }

            return filters;
        }, {});
}

const Container = styled.section`
    flex-direction: column;
    row-gap: ${Sizes.SPACING.HALF};
    
    > .toggle-expanded {
        align-self: center;
    }

    .filter {
        margin: 0;
    }
`;

export default observer(RateFilterSelector);
